// src/pages/StartPage.tsx
// Ad landing page at /start — matches the two Google/Meta ad creatives:
//   Ad 1: "Learn 3 languages in one month." (outcome hook → hero)
//   Ad 2: "Don't just read code. Run it."   (objection-killer → wedge section)
// Self-contained: scoped to .start-root with `s-` prefixed SVG ids + keyframes
// so it never collides with HomePage's theme. CTA → /login (new users auth,
// then the paywall guard routes them to /pay).

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LANGS = ['Python', 'Java', 'C++', 'JavaScript', 'Go', 'Rust', 'C', 'TypeScript'];

const StartPage: React.FC = () => {
  const navigate = useNavigate();
  const [running, setRunning] = useState<'idle' | 'running' | 'done'>('idle');
  const [runtime, setRuntime] = useState<number | null>(null);
  const runStartRef = useRef<number>(0);

  // v3.19.7.3: track UTM-tagged ad visits on /start (mirrors LandingPageIndia.tsx:33-61).
  // POSTs to /api/ad-visit which inserts a row in ad_visits and returns visit_id; we stash
  // it in localStorage so the backend can link it to the user record on signup
  // (UPDATE ad_visits SET user_id=..., converted=true in main.py).
  // Without this, /start ad traffic is invisible to the ad-attribution SQL — only GA4 sees it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (!hasUtm) return;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiUrl}/api/ad-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        landing_page: '/start',
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        utm_term: params.get('utm_term') || undefined,
        gclid: params.get('gclid') || undefined,
        referrer: document.referrer || undefined,
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.visit_id) {
          localStorage.setItem('ad_visit_id', String(data.visit_id));
        }
      })
      .catch(() => { /* tracking is best-effort; never block the landing page */ });
  }, []);

  const goStart = () => navigate('/login');

  const runDemo = () => {
    if (running !== 'idle') return;
    setRunning('running');
    runStartRef.current = performance.now();
    setTimeout(() => {
      setRuntime(Math.round(performance.now() - runStartRef.current));
      setRunning('done');
      setTimeout(() => setRunning('idle'), 1400);
    }, 750);
  };

  return (
    <div className="start-root">
      <style>{`
        .start-root {
          --bg-cream: #fefae8;
          --bg-cream-2: #fff5d1;
          --bg-card: #ffffff;
          --ink: #1a1a2e;
          --ink-soft: #5a5a72;
          --ink-mute: #8a8aa3;
          --brand-purple: #7c7cff;
          --brand-pink: #d166e8;
          --brand-gradient: linear-gradient(135deg, #7c7cff 0%, #a065f0 50%, #d166e8 100%);
          --border-soft: #1a1a2e15;
          --code-bg: #1a1525;
          --code-text: #e8e3f0;
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--bg-cream);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
        }
        .start-root .mono { font-family: 'JetBrains Mono', monospace; }
        .start-root .brand-text {
          background: var(--brand-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .start-root .brand-bg { background: var(--brand-gradient); }
        .start-root .card-soft {
          background: var(--bg-card);
          border: 1.5px solid var(--border-soft);
          border-radius: 20px;
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 8px 24px -10px rgba(26,26,46,.08);
        }
        .start-root .code-block {
          background: var(--code-bg);
          color: var(--code-text);
          border-radius: 12px;
          padding: 16px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          line-height: 1.7;
          box-shadow: 0 8px 32px -8px rgba(26,21,37,.4);
          white-space: pre;
        }
        @keyframes s-float-1 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-14px) rotate(2deg); } }
        @keyframes s-float-2 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-10px) rotate(-3deg); } }
        @keyframes s-float-3 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-18px) rotate(4deg); } }
        .start-root .float-1 { animation: s-float-1 7s ease-in-out infinite; }
        .start-root .float-2 { animation: s-float-2 9s ease-in-out infinite; }
        .start-root .float-3 { animation: s-float-3 6s ease-in-out infinite; }
        @keyframes s-blink { 0%,92%,100% { transform: scaleY(1); } 94%,98% { transform: scaleY(0.1); } }
        .start-root .blink { animation: s-blink 5s infinite; transform-origin: center; transform-box: fill-box; }
        @keyframes s-spin { to { transform: rotate(360deg); } }
        .start-root .s-spin { animation: s-spin 1s linear infinite; display: inline-block; }
        .start-root .dots {
          background-image: radial-gradient(circle, #1a1a2e15 1px, transparent 1px);
          background-size: 28px 28px;
        }
        .start-root .num-cube {
          background: var(--brand-gradient);
          color: white;
          font-weight: 800;
          box-shadow: 0 8px 24px -8px #7c7cff80;
        }
        .start-root .lang-tile {
          background: #ffffff;
          border: 1.5px solid #1a1a2e12;
          border-radius: 14px;
          box-shadow: 0 10px 24px -12px rgba(26,26,46,.25);
        }
      `}</style>

      {/* SVG symbols (s- prefixed, isolated from HomePage) */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <symbol id="s-cube" viewBox="0 0 100 100">
            <polygon points="50,5 95,30 50,55 5,30" fill="#d166e8" />
            <polygon points="5,30 5,75 50,100 50,55" fill="#7c7cff" />
            <polygon points="95,30 95,75 50,100 50,55" fill="#a065f0" />
          </symbol>
          <symbol id="s-cube-soft" viewBox="0 0 100 100">
            <polygon points="50,5 95,30 50,55 5,30" fill="#f0d1ff" />
            <polygon points="5,30 5,75 50,100 50,55" fill="#c5c5ff" />
            <polygon points="95,30 95,75 50,100 50,55" fill="#dab5fa" />
          </symbol>
          <symbol id="s-smiley" viewBox="0 0 100 100">
            <rect x="6" y="6" width="88" height="88" rx="22" fill="url(#s-smileyGrad)" />
            <circle className="blink" cx="36" cy="42" r="6" fill="#fff" />
            <circle className="blink" cx="64" cy="42" r="6" fill="#fff" />
            <path d="M 32 62 Q 50 78 68 62" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none" />
          </symbol>
          <linearGradient id="s-smileyGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#7c7cff" />
            <stop offset="1" stopColor="#d166e8" />
          </linearGradient>
        </defs>
      </svg>

      {/* ───── MINIMAL NAV (logo + single CTA — no nav links to avoid leaks) ───── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b" style={{ background: 'rgba(254,250,232,0.85)', borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <svg className="w-8 h-8"><use href="#s-smiley" /></svg>
            <span className="font-bold tracking-tight text-lg">Cubemate</span>
          </a>
          <button onClick={goStart} className="px-4 py-2 text-sm font-semibold rounded-full brand-bg text-white hover:opacity-90 transition shadow-md">
            Start free →
          </button>
        </div>
      </nav>

      {/* ───── HERO (Ad 1: "Learn 3 languages in one month") ───── */}
      <header className="relative pt-28 pb-16 overflow-hidden">
        <svg className="absolute bottom-4 left-[42%] w-12 h-12 opacity-70 float-1 hidden md:block"><use href="#s-cube" /></svg>
        <svg className="absolute bottom-10 right-[6%] w-16 h-16 opacity-80 float-2 hidden lg:block"><use href="#s-cube-soft" /></svg>
        <div
          className="absolute inset-0 dots opacity-50 pointer-events-none"
          style={{
            maskImage: 'radial-gradient(ellipse 80% 60% at 30% 20%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 30% 20%, black, transparent)',
          }}
        ></div>

        <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: copy + CTA */}
          <div className="text-center lg:text-left">
            <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-[1.02] mb-6">
              Learn <span className="brand-text">3 languages</span><br />
              in one month.
            </h1>

            <p className="text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed mb-8" style={{ color: 'var(--ink-soft)' }}>
              AI tutorials that teach you — then run every line <span className="font-semibold" style={{ color: 'var(--ink)' }}>right in your browser</span>.
              Python, Java, C++ and 5 more. Zero install, zero setup.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start items-center">
              <button onClick={goStart} className="px-7 py-3.5 font-semibold rounded-full brand-bg text-white hover:opacity-90 transition shadow-lg">
                Start free →
              </button>
              <a href="#run" className="px-7 py-3.5 font-semibold rounded-full bg-white border-2 hover:border-[#1a1a2e]/30 transition" style={{ borderColor: '#1a1a2e1a' }}>
                See it run
              </a>
            </div>

            <p className="mt-5 text-sm" style={{ color: 'var(--ink-mute)' }}>6 days free · no card · upgrade to Pro ₹249/mo when ready</p>
          </div>

          {/* Right: ad visual brought to life (3 language cubes + mascot + terminal) */}
          <div className="relative">
            <div className="card-soft p-7 relative">
              <div className="flex items-center gap-5">
                {/* stacked language tiles */}
                <div className="flex flex-col gap-3">
                  {[['Python', '-6deg'], ['Java', '4deg'], ['C++', '-3deg']].map(([lang, rot]) => (
                    <div key={lang} className="lang-tile px-4 py-2.5 w-28 text-center" style={{ transform: `rotate(${rot})` }}>
                      <span className="font-bold text-sm">{lang}</span>
                    </div>
                  ))}
                </div>
                {/* mascot */}
                <svg className="w-28 h-28 float-3"><use href="#s-smiley" /></svg>
              </div>

              {/* terminal: three languages, one Hello each */}
              <pre className="code-block overflow-x-auto mt-6 text-[11px]">{`> python   print("Hello, World")
> java     System.out.println("Hello, World");
> c++      std::cout << "Hello, World";`}</pre>
              <p className="text-center text-xs mt-3 mono" style={{ color: 'var(--ink-mute)' }}>✓ three languages · one workspace</p>
            </div>
          </div>
        </div>
      </header>

      {/* ───── LANGUAGE STRIP ───── */}
      <section className="pb-4">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-xs mono uppercase tracking-wider font-semibold mb-4" style={{ color: 'var(--ink-mute)' }}>run any of these — instantly</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {LANGS.map(l => (
              <span key={l} className="px-4 py-1.5 rounded-full bg-white border text-sm font-medium" style={{ borderColor: '#1a1a2e14', color: 'var(--ink-soft)' }}>{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ───── THE WEDGE (Ad 2: "Don't just read code. Run it.") + live demo ───── */}
      <section id="run" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>why not just use a chatbot?</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">
              Don't just read code. <span className="brand-text">Run it.</span>
            </h2>
            <p className="mt-4" style={{ color: 'var(--ink-soft)' }}>
              Most AI gives you code you can't trust. Cubemate runs it, explains it, and turns it into a tutorial you keep. Try the button below — it really executes.
            </p>
          </div>

          <div className="card-soft overflow-hidden p-0 max-w-3xl mx-auto">
            <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#fff5d166', borderColor: '#1a1a2e14' }}>
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: '#f87171' }}></span>
                <span className="w-3 h-3 rounded-full" style={{ background: '#facc15' }}></span>
                <span className="w-3 h-3 rounded-full" style={{ background: '#34d399' }}></span>
              </div>
              <span className="ml-3 text-xs mono" style={{ color: 'var(--ink-mute)' }}>cubemate — Chat</span>
            </div>

            <div className="p-8 space-y-4" style={{ background: 'white' }}>
              <div className="inline-flex items-center gap-2">
                <svg className="w-7 h-7"><use href="#s-smiley" /></svg>
                <span className="text-xs mono uppercase tracking-wider" style={{ color: 'var(--ink-mute)' }}>your first lesson</span>
              </div>
              <p className="text-lg leading-snug font-medium">
                A simple FizzBuzz — explained, then executed.
              </p>
              <pre className="code-block overflow-x-auto">{`for i in range(1, 16):
    if i % 15 == 0:
        print("FizzBuzz")
    elif i % 3 == 0:
        print("Fizz")
    elif i % 5 == 0:
        print("Buzz")
    else:
        print(i)`}</pre>

              <div
                className="overflow-hidden transition-all duration-500"
                style={{ opacity: running === 'idle' ? 0 : 1, maxHeight: running === 'idle' ? 0 : 260 }}
              >
                <div className="mt-3 border-l-4 pl-3 py-1" style={{ borderColor: '#34d399' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] mono uppercase tracking-wider font-bold" style={{ color: '#059669' }}>output</span>
                    {runtime !== null && (
                      <span className="text-[10px] mono" style={{ color: 'var(--ink-mute)' }}>· {runtime} ms</span>
                    )}
                  </div>
                  <pre className="mono text-xs leading-relaxed rounded p-2" style={{ background: '#d1fae566', color: 'var(--ink)' }}>{`1
2
Fizz
4
Buzz
Fizz
7
8
Fizz
Buzz
11
Fizz
13
14
FizzBuzz`}</pre>
                </div>
              </div>

              <div className="flex gap-2 pt-1 items-center">
                <button
                  onClick={runDemo}
                  disabled={running !== 'idle'}
                  className="px-4 py-2 text-xs font-semibold rounded-full text-white transition flex items-center gap-1.5 shadow-md disabled:opacity-80 disabled:cursor-not-allowed"
                  style={{ background: '#10b981' }}
                >
                  <span className={running === 'running' ? 's-spin' : ''}>
                    {running === 'idle' ? '▶' : running === 'running' ? '◐' : '✓'}
                  </span>
                  <span>{running === 'idle' ? 'Run this code' : running === 'running' ? 'Running…' : 'Done'}</span>
                </button>
                {running === 'idle' && (
                  <span className="text-xs ml-1" style={{ color: 'var(--ink-mute)' }}>← click to execute</span>
                )}
              </div>
            </div>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: 'var(--ink-mute)' }}>
            Every code block runs in our cloud sandbox. No install. No setup.
          </p>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, transparent, #fff5d14d, transparent)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>how it works</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Pick 3. Learn by running.</h2>
            <p className="mt-4" style={{ color: 'var(--ink-soft)' }}>No videos to sit through. No environment to install. Just ask, run, repeat.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-9 left-[20%] right-[20%] h-px border-t-2 border-dashed" style={{ borderColor: '#1a1a2e26' }}></div>
            {[
              ['1', 'Ask', '“Teach me Python from scratch.” Cubemate writes a book-style tutorial with runnable examples.'],
              ['2', 'Run', 'Execute every example in the browser — see real output, tweak it, learn by doing.'],
              ['3', 'Next language', 'Repeat for Java, then C++. Three languages, one month, all in one place.'],
            ].map(([n, title, desc]) => (
              <div key={n} className="text-center relative">
                <div className="num-cube w-16 h-16 rounded-2xl grid place-items-center text-2xl mx-auto mb-5">{n}</div>
                <h3 className="font-bold mb-2 text-lg">{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── PRICING (honest, no surprise) ───── */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="card-soft p-8 md:p-10 text-center">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>simple pricing</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-2"><span className="brand-text">6 days free</span>.</h2>
            <p className="mb-6" style={{ color: 'var(--ink-soft)' }}>
              125 credits, no card. Chat + Bookmate tutorials included. Loved it? Pro is ₹249/month for 400 credits + Workflow + PDF sharing. Cancel anytime.
            </p>
            <button onClick={goStart} className="px-8 py-3.5 font-semibold rounded-full brand-bg text-white hover:opacity-90 transition shadow-lg">
              Start free →
            </button>
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-16 text-center text-white" style={{ background: 'var(--brand-gradient)' }}>
            <svg className="absolute -top-8 -right-6 w-40 h-40 opacity-40"><use href="#s-smiley" /></svg>
            <svg className="absolute -bottom-10 -left-6 w-32 h-32 opacity-30 float-1"><use href="#s-cube" /></svg>
            <div className="relative">
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5">Three languages. One month.</h2>
              <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">Start learning by running today — free for 6 days, no card needed.</p>
              <button onClick={goStart} className="px-8 py-3.5 font-bold rounded-full bg-white hover:bg-[#fefae8] transition" style={{ color: 'var(--ink)' }}>
                Start free →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FOOTER (minimal) ───── */}
      <footer className="border-t py-10" style={{ borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ink-mute)' }}>
          <div className="flex items-center gap-2.5">
            <svg className="w-7 h-7"><use href="#s-smiley" /></svg>
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Cubemate</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-[#1a1a2e] transition">Privacy</a>
            <a href="/terms" className="hover:text-[#1a1a2e] transition">Terms</a>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-mute)' }}>© 2026 Cubemate. Made in India.</p>
      </footer>
    </div>
  );
};

export default StartPage;
